import {
  Icons,
  notify,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { PreparedWorkload } from 'features/workload-optimization';
import { useUpdateWorkloadMutationV2 } from 'hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';

import { useConfirmationDialog } from './useConfirmationDialog';
import { isEnabledAutoAssignmentPolicy } from '../_utils';

type UseBatchAutoAssignmentToggleParams = {
  workloadIds: string[];
  workloads: PreparedWorkload[];
};

export const useBatchAutoAssignmentToggle = ({
  workloadIds,
  workloads,
}: UseBatchAutoAssignmentToggleParams) => {
  const { openConfirmationDialog, closeConfirmationDialog } =
    useConfirmationDialog();

  const { mutate, isPending } = useUpdateWorkloadMutationV2();

  // Filter to get only selected workloads that are not already using auto-assignment
  const eligibleWorkloads = workloads.filter(
    (w) =>
      workloadIds.includes(w.id) &&
      !isEnabledAutoAssignmentPolicy({ workload: w })
  );

  const canEnableAutoAssignmentCount = !!eligibleWorkloads.length;

  const enableBatchAutoAssignment = () => {
    openConfirmationDialog({
      title: 'Enable automatic assignment?',
      body: (
        <>
          <Typography
            variant="P14R"
            color={tokens.theme.fg.structural.secondary}
          >
            The workloads will be automatically assigned to their best matching
            policies. Assignments may change as rules or workload
            characteristics are updated.
          </Typography>
          <Box
            sx={{
              mt: 12,
              borderRadius: '4px',
            }}
          >
            <Stack
              sx={{
                m: '0 -65px',
                backgroundColor: tokens.theme.bg.status.infoSubtle,
                py: 12,
              }}
              direction="row"
              alignItems="center"
              justifyContent="center"
              color={tokens.theme.fg.status.onInfoSubtle}
              gap={8}
            >
              <Icons.Info weight="fill" />
              <Typography variant="P12M">
                Workloads already using automatic assignment will remain
                unchanged.
              </Typography>
            </Stack>
          </Box>
        </>
      ),
      variant: 'check',
      icon: <Icons.ArrowsClockwise />,
      confirmBtnLabel: 'Enable',
      confirmButtonProps: {
        onClick: () => {
          const requests = eligibleWorkloads.map((workload) => ({
            clusterId: workload.clusterId,
            workloadId: workload.id,
            workload: {
              scalingPolicyId: null, // null enables auto-assignment
            },
          }));

          mutate(requests, {
            onSuccess: () => {
              notify.success('Automatic assignment enabled');
              closeConfirmationDialog();
            },
            onError: () => {
              closeConfirmationDialog();
            },
          });
        },
      },
    });
  };

  return {
    enableBatchAutoAssignment,
    canEnableAutoAssignmentCount,
    isLoading: isPending,
  };
};
